import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { financeApi as F, INVOICE_STATUS_STYLE, inr } from "@/lib/finance";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, Loader2, FilePlus2, IndianRupee, Receipt } from "lucide-react";
import PaymentDialog from "@/pages/finance/PaymentDialog";

export default function Finance() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [tab, setTab] = useState("invoices");
  const canCreate = can("finance", "create");

  const loadStats = () => F.stats().then(setStats).catch(() => {});
  useEffect(() => { loadStats(); }, []);

  return (
    <div className="space-y-6" data-testid="finance-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Finance &amp; Accounts</h2>
          <p className="mt-1 text-sm text-slate-500">Invoices, receivables and payments. Only dispatched goods can be invoiced; posted invoices are immutable.</p>
        </div>
        {canCreate && <GenerateInvoiceButton onDone={loadStats} />}
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Card className="p-4" data-testid="fin-stat-receivable"><p className="text-xs uppercase tracking-wider text-slate-500">Receivable</p><p className="mt-1 font-heading text-xl font-bold text-rose-600">{inr(stats.total_receivable)}</p></Card>
        <Card className="p-4" data-testid="fin-stat-revenue"><p className="text-xs uppercase tracking-wider text-slate-500">Revenue (posted)</p><p className="mt-1 font-heading text-xl font-bold text-emerald-600">{inr(stats.total_revenue)}</p></Card>
        <Card className="p-4" data-testid="fin-stat-overdue"><p className="text-xs uppercase tracking-wider text-slate-500">Overdue invoices</p><p className="mt-1 font-heading text-xl font-bold text-amber-600">{stats.overdue_invoices ?? 0}</p></Card>
        <Card className="p-4" data-testid="fin-stat-advances"><p className="text-xs uppercase tracking-wider text-slate-500">Advances</p><p className="mt-1 font-heading text-xl font-bold text-indigo-600">{inr(stats.advances)}</p></Card>
        <Card className="p-4" data-testid="fin-stat-invoiceable"><p className="text-xs uppercase tracking-wider text-slate-500">To invoice</p><p className="mt-1 font-heading text-xl font-bold text-slate-900">{stats.invoiceable_orders ?? 0}</p></Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="finance-tabs">
          <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="receivables" data-testid="tab-receivables">Receivables</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports">GST &amp; Valuation</TabsTrigger>
        </TabsList>
        <TabsContent value="invoices" className="mt-4"><InvoicesTab navigate={navigate} /></TabsContent>
        <TabsContent value="payments" className="mt-4"><PaymentsTab navigate={navigate} canCreate={canCreate} onDone={loadStats} /></TabsContent>
        <TabsContent value="receivables" className="mt-4"><ReceivablesTab /></TabsContent>
        <TabsContent value="reports" className="mt-4"><ReportsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function GenerateInvoiceButton({ onDone }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [soNo, setSoNo] = useState("");
  const [selected, setSelected] = useState([]);
  const [tcs, setTcs] = useState("0");
  const [busy, setBusy] = useState(false);

  const openDlg = async () => { setGroups(await F.invoiceable()); setSoNo(""); setSelected([]); setTcs("0"); setOpen(true); };
  const group = groups.find((g) => g.so_no === soNo);
  const toggle = (id) => setSelected((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const create = async () => {
    if (selected.length === 0) { toast.error("Select at least one challan"); return; }
    setBusy(true);
    try { const r = await F.createInvoice({ challan_ids: selected, tcs_pct: Number(tcs) || 0 }); toast.success(`Invoice ${r.invoice_no} drafted`); setOpen(false); onDone?.(); navigate(`/finance/invoices/${r.id}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Button onClick={openDlg} data-testid="generate-invoice-btn" className="bg-indigo-600 text-white hover:bg-indigo-700"><FilePlus2 className="mr-2 h-4 w-4" />Generate Invoice</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Generate GST Invoice</DialogTitle><DialogDescription>Pick a Sales Order, then choose its dispatched challans (consolidate multiple into one invoice).</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Sales Order</Label>
              <Select value={soNo} onValueChange={(v) => { setSoNo(v); setSelected([]); }}>
                <SelectTrigger data-testid="invoice-so-select"><SelectValue placeholder="Order with dispatched, uninvoiced challans" /></SelectTrigger>
                <SelectContent>{groups.map((g) => <SelectItem key={g.so_no} value={g.so_no}>{g.so_no} · {g.customer} ({g.challans.length} challan)</SelectItem>)}</SelectContent>
              </Select>
              {groups.length === 0 && <p className="text-xs text-amber-600">No dispatched challans awaiting invoicing.</p>}
            </div>
            {group && (
              <div className="max-h-56 space-y-1 overflow-y-auto rounded-md border border-slate-200 p-2">
                {group.challans.map((c) => (
                  <label key={c.id} className="flex items-center justify-between rounded px-2 py-1.5 hover:bg-slate-50" data-testid={`invoiceable-${c.challan_no}`}>
                    <span className="flex items-center gap-2 text-sm"><Checkbox checked={selected.includes(c.id)} onCheckedChange={() => toggle(c.id)} data-testid={`sel-challan-${c.challan_no}`} />{c.challan_no} · {c.status}</span>
                    <span className="text-sm text-slate-500">{c.rolls} roll(s) · {c.meters} m</span>
                  </label>
                ))}
              </div>
            )}
            {group && <div className="space-y-1.5"><Label className="text-xs">TCS % (optional)</Label><Input type="number" value={tcs} onChange={(e) => setTcs(e.target.value)} className="w-32" data-testid="invoice-tcs-input" /></div>}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={create} disabled={selected.length === 0 || busy} data-testid="confirm-generate-invoice-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Generate (${selected.length})`}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function InvoicesTab({ navigate }) {
  const [meta, setMeta] = useState({ statuses: [] });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");

  const load = async () => { setLoading(true); try { setRows(await F.listInvoices({ search: search || undefined, status })); } finally { setLoading(false); } };
  useEffect(() => { F.meta().then(setMeta); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);

  return (
    <Card className="p-4">
      <div className="mb-4 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search invoice / SO / customer" className="pl-9" data-testid="inv-search-input" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-48" data-testid="inv-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{meta.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div> : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="text-xs uppercase text-slate-500">Invoice</TableHead>
              <TableHead className="text-xs uppercase text-slate-500">Customer</TableHead>
              <TableHead className="text-xs uppercase text-slate-500">Date</TableHead>
              <TableHead className="text-right text-xs uppercase text-slate-500">Total</TableHead>
              <TableHead className="text-right text-xs uppercase text-slate-500">Balance</TableHead>
              <TableHead className="text-xs uppercase text-slate-500">Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} className="cursor-pointer" onClick={() => navigate(`/finance/invoices/${r.id}`)} data-testid={`invoice-row-${r.invoice_no}`}>
                  <TableCell className="font-medium text-slate-800">{r.invoice_no}<span className="block text-xs text-slate-400">{r.so_no}</span></TableCell>
                  <TableCell className="text-slate-700">{r.customer?.company_name}</TableCell>
                  <TableCell className="text-slate-600">{r.invoice_date}</TableCell>
                  <TableCell className="text-right tabular-nums text-slate-700">{inr(r.totals?.grand_total)}</TableCell>
                  <TableCell className="text-right tabular-nums text-slate-700">{inr(r.balance_amount)}</TableCell>
                  <TableCell><Badge className={`hover:bg-inherit ${INVOICE_STATUS_STYLE[r.status]}`}>{r.status}</Badge></TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={6} className="py-12 text-center text-slate-400" data-testid="invoices-empty">No invoices yet. Generate one from a dispatched challan.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}
    </Card>
  );
}

function PaymentsTab({ navigate, canCreate, onDone }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [payOpen, setPayOpen] = useState(false);
  const load = async () => { setLoading(true); try { setRows(await F.listPayments({ search: search || undefined })); } finally { setLoading(false); } };
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);

  return (
    <Card className="p-4">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search receipt / customer / reference" className="pl-9" data-testid="pay-search-input" />
        </div>
        {canCreate && <Button onClick={() => setPayOpen(true)} data-testid="record-payment-btn" className="bg-indigo-600 text-white hover:bg-indigo-700"><IndianRupee className="mr-2 h-4 w-4" />Record Payment</Button>}
      </div>
      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div> : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="text-xs uppercase text-slate-500">Receipt</TableHead>
              <TableHead className="text-xs uppercase text-slate-500">Customer</TableHead>
              <TableHead className="text-xs uppercase text-slate-500">Mode</TableHead>
              <TableHead className="text-right text-xs uppercase text-slate-500">Amount</TableHead>
              <TableHead className="text-right text-xs uppercase text-slate-500">Advance</TableHead>
              <TableHead className="text-xs uppercase text-slate-500">Allocated to</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`payment-row-${r.payment_no}`}>
                  <TableCell className="font-medium text-slate-800">{r.payment_no}<span className="block text-xs text-slate-400">{r.payment_date}</span></TableCell>
                  <TableCell className="text-slate-700">{r.customer}</TableCell>
                  <TableCell className="text-slate-600">{r.mode}{r.reference ? ` · ${r.reference}` : ""}</TableCell>
                  <TableCell className="text-right tabular-nums text-slate-700">{inr(r.amount)}{r.tds > 0 && <span className="block text-xs text-slate-400">TDS {inr(r.tds)}</span>}</TableCell>
                  <TableCell className="text-right tabular-nums text-indigo-600">{r.advance > 0 ? inr(r.advance) : "—"}</TableCell>
                  <TableCell className="text-slate-600">{(r.allocations || []).map((a) => a.invoice_no).join(", ") || "—"}</TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={6} className="py-12 text-center text-slate-400" data-testid="payments-empty">No payments recorded yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}
      <PaymentDialog open={payOpen} onOpenChange={setPayOpen} onDone={() => { load(); onDone?.(); }} />
    </Card>
  );
}

function ReceivablesTab() {
  const [out, setOut] = useState([]);
  const [aging, setAging] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { Promise.all([F.outstanding(), F.aging()]).then(([o, a]) => { setOut(o); setAging(a); }).finally(() => setLoading(false)); }, []);
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;
  return (
    <div className="space-y-6">
      <Card className="p-4">
        <h3 className="mb-3 font-heading text-base font-semibold text-slate-900">Receivables aging</h3>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-6" data-testid="aging-summary">
          {(aging?.buckets || []).map((b) => (
            <div key={b} className="rounded-md border border-slate-200 p-3"><p className="text-xs uppercase text-slate-500">{b === "Current" ? "Not due" : `${b} days`}</p><p className="mt-1 font-heading text-sm font-bold text-slate-900">{inr(aging.summary[b])}</p></div>
          ))}
          <div className="rounded-md border border-indigo-200 bg-indigo-50 p-3"><p className="text-xs uppercase text-indigo-500">Total</p><p className="mt-1 font-heading text-sm font-bold text-indigo-700">{inr(aging.summary.total)}</p></div>
        </div>
      </Card>
      <Card className="p-4">
        <h3 className="mb-3 font-heading text-base font-semibold text-slate-900">Customer outstanding</h3>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="text-xs uppercase text-slate-500">Customer</TableHead>
              <TableHead className="text-xs uppercase text-slate-500">GSTIN</TableHead>
              <TableHead className="text-right text-xs uppercase text-slate-500">Open invoices</TableHead>
              <TableHead className="text-right text-xs uppercase text-slate-500">Outstanding</TableHead>
              <TableHead className="text-right text-xs uppercase text-slate-500">Advance</TableHead>
              <TableHead className="text-right text-xs uppercase text-slate-500">Net due</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {out.map((r) => (
                <TableRow key={r.customer_id} data-testid={`outstanding-row-${r.customer_id}`}>
                  <TableCell className="font-medium text-slate-800">{r.customer}</TableCell>
                  <TableCell className="text-slate-500">{r.gstin || "—"}</TableCell>
                  <TableCell className="text-right tabular-nums text-slate-700">{r.invoices}</TableCell>
                  <TableCell className="text-right tabular-nums text-slate-700">{inr(r.outstanding)}</TableCell>
                  <TableCell className="text-right tabular-nums text-indigo-600">{inr(r.advance)}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold text-rose-600">{inr(r.net_outstanding)}</TableCell>
                </TableRow>
              ))}
              {out.length === 0 && <TableRow><TableCell colSpan={6} className="py-10 text-center text-slate-400">No outstanding receivables.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}

function ReportsTab() {
  const [gst, setGst] = useState(null);
  const [val, setVal] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { Promise.all([F.gstSummary(), F.valuation()]).then(([g, v]) => { setGst(g); setVal(v); }).finally(() => setLoading(false)); }, []);
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card className="p-6" data-testid="gst-summary-card">
        <h3 className="mb-4 flex items-center gap-2 font-heading text-base font-semibold text-slate-900"><Receipt className="h-4 w-4 text-indigo-600" />GST summary (posted invoices)</h3>
        <dl className="space-y-2 text-sm">
          <Row label="Taxable value" value={inr(gst.taxable_value)} />
          <Row label="CGST" value={inr(gst.cgst)} />
          <Row label="SGST" value={inr(gst.sgst)} />
          <Row label="IGST" value={inr(gst.igst)} />
          <Row label="TCS" value={inr(gst.tcs)} />
          <Row label="Total tax" value={inr(gst.total_tax)} bold />
          <Row label="Invoiced (grand total)" value={inr(gst.grand_total)} bold />
          <Row label="Intra / Inter invoices" value={`${gst.intra_invoices} / ${gst.inter_invoices}`} />
        </dl>
      </Card>
      <Card className="p-6" data-testid="valuation-card">
        <h3 className="mb-4 font-heading text-base font-semibold text-slate-900">Inventory valuation (at order rate)</h3>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="text-xs uppercase text-slate-500">Status</TableHead>
              <TableHead className="text-right text-xs uppercase text-slate-500">Items</TableHead>
              <TableHead className="text-right text-xs uppercase text-slate-500">Meters</TableHead>
              <TableHead className="text-right text-xs uppercase text-slate-500">Value</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(val.by_status || []).map((r) => (
                <TableRow key={r.status}><TableCell className="text-slate-700">{r.status}</TableCell><TableCell className="text-right tabular-nums text-slate-600">{r.items}</TableCell><TableCell className="text-right tabular-nums text-slate-600">{r.meters}</TableCell><TableCell className="text-right tabular-nums text-slate-700">{inr(r.value)}</TableCell></TableRow>
              ))}
              <TableRow className="bg-slate-50"><TableCell className="font-semibold text-slate-800">Total</TableCell><TableCell /><TableCell className="text-right tabular-nums font-semibold text-slate-800">{val.total_meters}</TableCell><TableCell className="text-right tabular-nums font-semibold text-slate-800">{inr(val.total_value)}</TableCell></TableRow>
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}

const Row = ({ label, value, bold }) => (
  <div className="flex justify-between border-b border-slate-100 py-1"><dt className="text-slate-500">{label}</dt><dd className={`tabular-nums ${bold ? "font-semibold text-slate-900" : "text-slate-700"}`}>{value}</dd></div>
);
